import { useCallback, useEffect, useState } from 'react';
import { crmService } from '../../services/crm.service';
import { crmErrorMessage } from '../../utils/crm';

/**
 * Loads one person with their activity and tasks, and does the things a lead's page does to them.
 * Every change goes through the CRM service and then reloads, so the page only ever shows what the
 * service last returned. Each action resolves true when it worked.
 */
export const useLead = ({ book, ownerUserId, personId, enabled = true, onChanged = () => {} }) => {
  const [person, setPerson] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [status, setStatus] = useState('loading');   // loading | ready | error
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, t, taskList] = await Promise.all([
        crmService.getPerson(book, personId, ownerUserId),
        crmService.getActivities(book, personId, ownerUserId),
        crmService.getTasks(book, personId, ownerUserId),
      ]);
      setPerson(p);
      setTimeline(t);
      setTasks(taskList);
      setError('');
      setStatus('ready');
    } catch (err) {
      setError(crmErrorMessage(err));
      setStatus((prev) => (prev === 'ready' ? prev : 'error'));
    }
  }, [book, personId, ownerUserId]);

  // `enabled` is false for a role that may not use the CRM: nothing is requested at all.
  useEffect(() => {
    if (!enabled) return;
    setStatus('loading');
    load();
  }, [load, enabled]);

  const run = useCallback(async (work) => {
    setBusy(true);
    setError('');
    try {
      await work();
      await load();
      onChanged();
      return true;
    } catch (err) {
      setError(crmErrorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  }, [load, onChanged]);

  const changeLead = (side, body) => run(() => crmService.updateLead(book, personId, side, body, ownerUserId));

  /** body null removes the task. */
  const actOnTask = (task, body) => run(() => (body === null
    ? crmService.deleteTask(book, personId, task.id, ownerUserId)
    : crmService.updateTask(book, personId, task.id, body, ownerUserId)));

  const addTask = async (task) => {
    if (!task.title.trim()) {
      setError('Give the task a title.');
      return false;
    }
    return run(() => crmService.createTask(book, personId, {
      title: task.title.trim(),
      type: task.type,
      dueDate: task.dueDate,
      ...(task.side && { side: task.side }),
    }, ownerUserId));
  };

  const addEntry = async (entry) => {
    if (entry.type === 'NOTE' && !entry.text.trim()) {
      setError('Write the note first.');
      return false;
    }
    return run(() => crmService.logActivity(book, personId, {
      type: entry.type,
      ...(entry.text.trim() && { text: entry.text.trim() }),
      ...(entry.followUp && { nextFollowUpDate: entry.followUp }),
      ...(entry.side && { side: entry.side }),
    }, ownerUserId));
  };

  return { person, timeline, tasks, status, error, setError, busy, load, changeLead, actOnTask, addTask, addEntry };
};
